import { PDFDocument, StandardFonts, rgb, degrees } from 'pdf-lib'
import { jsPDF } from 'jspdf'
import { baseName } from './format'

// ---- pdfjs (lazy-loaded; worker served from /public) ----
let pdfjsLib = null
export async function getPdfjs() {
  if (pdfjsLib) return pdfjsLib
  pdfjsLib = await import('pdfjs-dist')
  pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`
  return pdfjsLib
}

async function readDoc(file) {
  const bytes = await file.arrayBuffer()
  return PDFDocument.load(bytes, { ignoreEncryption: true })
}

export async function mergePdfs(files) {
  const out = await PDFDocument.create()
  for (const f of files) {
    const src = await readDoc(f)
    const pages = await out.copyPages(src, src.getPageIndices())
    pages.forEach((p) => out.addPage(p))
  }
  const bytes = await out.save()
  return { blob: new Blob([bytes], { type: 'application/pdf' }), name: 'merged.pdf', mime: 'application/pdf' }
}

// Parse "1-3, 5, 8" into zero-based page indices, bounded by total.
function parseRanges(str, total) {
  if (!str || !str.trim()) return Array.from({ length: total }, (_, i) => i)
  const set = new Set()
  str.split(',').forEach((part) => {
    const p = part.trim()
    if (!p) return
    const m = p.match(/^(\d+)\s*-\s*(\d+)$/)
    if (m) {
      let a = +m[1]
      let b = +m[2]
      if (a > b) [a, b] = [b, a]
      for (let i = a; i <= b; i++) if (i >= 1 && i <= total) set.add(i - 1)
    } else if (/^\d+$/.test(p)) {
      const n = +p
      if (n >= 1 && n <= total) set.add(n - 1)
    }
  })
  return [...set].sort((a, b) => a - b)
}

export async function splitPdf(file, opts) {
  const src = await readDoc(file)
  const total = src.getPageCount()
  const indices = parseRanges(opts.pages, total)
  if (!indices.length) throw new Error('No valid pages selected.')
  const out = await PDFDocument.create()
  const pages = await out.copyPages(src, indices)
  pages.forEach((p) => out.addPage(p))
  const bytes = await out.save()
  return {
    blob: new Blob([bytes], { type: 'application/pdf' }),
    name: `${baseName(file.name)}-pages.pdf`,
    mime: 'application/pdf',
  }
}

// Real compression: render each page and re-encode it as a JPEG at the chosen
// quality, then rebuild the PDF. This shrinks image-heavy / scanned PDFs a lot.
// If the result ever comes out larger (e.g. a tiny text-only PDF), the original
// bytes are kept so the file never grows.
export async function compressPdf(file, opts) {
  const level = (opts && opts.level) || 'Recommended'
  const cfg = {
    Light: { scale: 1.6, q: 0.72 },
    Recommended: { scale: 1.3, q: 0.6 },
    Strong: { scale: 1.0, q: 0.45 },
  }[level] || { scale: 1.3, q: 0.6 }

  const lib = await getPdfjs()
  const data = await file.arrayBuffer()
  const pdf = await lib.getDocument({ data }).promise

  let doc = null
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i)
    const viewport = page.getViewport({ scale: cfg.scale })
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.floor(viewport.width))
    canvas.height = Math.max(1, Math.floor(viewport.height))
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    await page.render({ canvasContext: ctx, viewport }).promise
    const jpeg = canvas.toDataURL('image/jpeg', cfg.q)

    // Page size in points (1/72") so the output keeps the real dimensions.
    const vp1 = page.getViewport({ scale: 1 })
    const w = vp1.width
    const h = vp1.height
    const orient = w > h ? 'l' : 'p'
    if (!doc) doc = new jsPDF({ orientation: orient, unit: 'pt', format: [w, h] })
    else doc.addPage([w, h], orient)
    doc.addImage(jpeg, 'JPEG', 0, 0, w, h)
  }

  let blob = doc ? doc.output('blob') : file
  // Never return something bigger than the source.
  if (blob.size >= file.size) {
    const src = await readDoc(file)
    const bytes = await src.save({ useObjectStreams: true })
    const resave = new Blob([bytes], { type: 'application/pdf' })
    blob = resave.size < file.size ? resave : file
  }

  return {
    blob,
    name: `${baseName(file.name)}-compressed.pdf`,
    mime: 'application/pdf',
  }
}

// opts.overlay = { dataUrl, page } — a transparent PNG produced by the
// interactive editor (text, images and drawings), sized to the page. It is
// stamped over the matching page so the output matches the editor exactly.
export async function editPdf(file, opts) {
  const src = await readDoc(file)
  const pages = src.getPages()
  if (!opts.overlay?.dataUrl) throw new Error('Add some text, an image or a drawing first.')
  const pageIndex = Math.min(Math.max(0, (opts.overlay.page || 1) - 1), pages.length - 1)
  const page = pages[pageIndex]
  const { width, height } = page.getSize()
  const png = await src.embedPng(await fetch(opts.overlay.dataUrl).then((r) => r.arrayBuffer()))
  page.drawImage(png, { x: 0, y: 0, width, height })
  const bytes = await src.save()
  return {
    blob: new Blob([bytes], { type: 'application/pdf' }),
    name: `${baseName(file.name)}-edited.pdf`,
    mime: 'application/pdf',
  }
}

// opts.signature is a PNG data URL drawn by the user. opts.placements is an
// array of { page, xRatio, yRatio, wRatio } — one per spot the user dropped the
// signature (it can be on many pages), with x/y as the top-left ratio.
export async function signPdf(file, opts) {
  const src = await readDoc(file)
  if (!opts.signature) throw new Error('Please draw a signature first.')
  const places = opts.placements || []
  if (!places.length) throw new Error('Place the signature on at least one page.')

  const pngBytes = await fetch(opts.signature).then((r) => r.arrayBuffer())
  const png = await src.embedPng(pngBytes)
  const pages = src.getPages()

  places.forEach((pl) => {
    const idx = Math.min(Math.max(0, pl.page || 0), pages.length - 1)
    const page = pages[idx]
    const { width: pw, height: ph } = page.getSize()
    const w = pl.wRatio * pw
    const h = w * (png.height / png.width)
    const x = pl.xRatio * pw
    const y = ph - pl.yRatio * ph - h // top-left ratio → bottom-left origin
    page.drawImage(png, { x, y, width: w, height: h })
  })

  const bytes = await src.save()
  return {
    blob: new Blob([bytes], { type: 'application/pdf' }),
    name: `${baseName(file.name)}-signed.pdf`,
    mime: 'application/pdf',
  }
}

// Real password encryption via @cantoo/pdf-lib (a pdf-lib fork with crypto).
// userPassword forces the reader to enter the password to OPEN the file.
export async function protectPdf(file, opts) {
  const password = (opts.password || '').trim()
  if (!password) throw new Error('Please set a password.')

  const { PDFDocument: CantooPDFDocument } = await import('@cantoo/pdf-lib')
  const bytes = await file.arrayBuffer()
  const doc = await CantooPDFDocument.load(bytes, { ignoreEncryption: true })
  doc.encrypt({
    userPassword: password, // required to open the document
    ownerPassword: password,
    permissions: { printing: 'highResolution', copying: true, modifying: false },
  })
  // Object streams aren't used so the encrypted output stays broadly compatible.
  const out = await doc.save({ useObjectStreams: false })
  return {
    blob: new Blob([out], { type: 'application/pdf' }),
    name: `${baseName(file.name)}-protected.pdf`,
    mime: 'application/pdf',
    note: 'Password set this PDF now asks for the password to open.',
  }
}

// Remove the password from a PDF the user can open (decrypt + re-save plain).
export async function unlockPdf(file, opts) {
  const { PDFDocument: CantooPDFDocument } = await import('@cantoo/pdf-lib')
  const bytes = await file.arrayBuffer()
  let doc
  try {
    doc = await CantooPDFDocument.load(bytes, { password: opts.password || '' })
  } catch (e) {
    throw new Error('Wrong password, or this PDF could not be opened.')
  }
  const out = await doc.save() // no encrypt() call → saved without protection
  return {
    blob: new Blob([out], { type: 'application/pdf' }),
    name: `${baseName(file.name)}-unlocked.pdf`,
    mime: 'application/pdf',
  }
}

// Stamp a text watermark across every page.
export async function watermarkPdf(file, opts) {
  const src = await readDoc(file)
  const font = await src.embedFont(StandardFonts.HelveticaBold)
  const text = opts.text || 'CONFIDENTIAL'
  const tiled = opts.position === 'Tiled'

  src.getPages().forEach((page) => {
    const { width, height } = page.getSize()
    if (tiled) {
      const size = Math.max(16, Math.min(width, height) / 14)
      const tw = font.widthOfTextAtSize(text, size)
      for (let y = 0; y < height + tw; y += tw * 1.3) {
        for (let x = -tw; x < width; x += tw * 1.8) {
          page.drawText(text, { x, y, size, font, color: rgb(0.6, 0.6, 0.6), opacity: 0.16, rotate: degrees(45) })
        }
      }
    } else {
      const size = Math.max(22, Math.min(width, height) / 7)
      const tw = font.widthOfTextAtSize(text, size)
      const rad = Math.PI / 4
      page.drawText(text, {
        x: width / 2 - (tw / 2) * Math.cos(rad),
        y: height / 2 - (tw / 2) * Math.sin(rad),
        size,
        font,
        color: rgb(0.5, 0.5, 0.5),
        opacity: 0.28,
        rotate: degrees(45),
      })
    }
  })

  const out = await src.save()
  return { blob: new Blob([out], { type: 'application/pdf' }), name: `${baseName(file.name)}-watermarked.pdf`, mime: 'application/pdf' }
}

// Rotate every page by 90 / 180 / 270 degrees.
export async function rotatePdf(file, opts) {
  const src = await readDoc(file)
  const add = parseInt(opts.angle || '90', 10) || 90
  src.getPages().forEach((p) => {
    const cur = p.getRotation().angle || 0
    p.setRotation(degrees((cur + add) % 360))
  })
  const out = await src.save()
  return { blob: new Blob([out], { type: 'application/pdf' }), name: `${baseName(file.name)}-rotated.pdf`, mime: 'application/pdf' }
}

// Stamp page numbers on every page.
export async function addPageNumbers(file, opts) {
  const src = await readDoc(file)
  const font = await src.embedFont(StandardFonts.Helvetica)
  const pages = src.getPages()
  const pos = opts.position || 'Bottom center'
  const size = 11
  const margin = 28
  pages.forEach((page, i) => {
    const { width, height } = page.getSize()
    const label = opts.format === 'Page X of N' ? `Page ${i + 1} of ${pages.length}` : `${i + 1}`
    const tw = font.widthOfTextAtSize(label, size)
    let x = width / 2 - tw / 2
    let y = margin
    if (pos === 'Bottom right') { x = width - tw - margin; y = margin }
    else if (pos === 'Top right') { x = width - tw - margin; y = height - margin - size }
    else if (pos === 'Top center') { x = width / 2 - tw / 2; y = height - margin - size }
    page.drawText(label, { x, y, size, font, color: rgb(0.3, 0.3, 0.3) })
  })
  const out = await src.save()
  return { blob: new Blob([out], { type: 'application/pdf' }), name: `${baseName(file.name)}-numbered.pdf`, mime: 'application/pdf' }
}

export async function pdfToImages(file, opts) {
  const lib = await getPdfjs()
  const data = await file.arrayBuffer()
  const pdf = await lib.getDocument({ data }).promise
  const scale = { Standard: 1.3, High: 2, Max: 3 }[opts.quality || 'High'] || 2
  const blobs = []
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i)
    const viewport = page.getViewport({ scale })
    const canvas = document.createElement('canvas')
    canvas.width = viewport.width
    canvas.height = viewport.height
    const ctx = canvas.getContext('2d')
    await page.render({ canvasContext: ctx, viewport }).promise
    const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.92))
    blobs.push(blob)
  }
  // Return first page as the preview/primary download; expose all for zip.
  return {
    blob: blobs[0],
    name: `${baseName(file.name)}-page1.jpg`,
    mime: 'image/jpeg',
    pages: blobs,
    multiOut: true,
  }
}

// Render a PDF page to a canvas image + its pixel dimensions. Used by the
// interactive Edit-PDF editor so overlays map exactly onto the page.
export async function renderPdfPage(file, scale = 2, pageIndex = 0) {
  const lib = await getPdfjs()
  const data = await file.arrayBuffer()
  const pdf = await lib.getDocument({ data }).promise
  const page = await pdf.getPage(pageIndex + 1)
  const viewport = page.getViewport({ scale })
  const canvas = document.createElement('canvas')
  canvas.width = viewport.width
  canvas.height = viewport.height
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  await page.render({ canvasContext: ctx, viewport }).promise
  return { dataUrl: canvas.toDataURL('image/png'), width: canvas.width, height: canvas.height }
}

// Render every page of a PDF to JPEG data URLs — used by the multi-page Sign
// view so the user can drop the signature on any page.
export async function renderAllPages(file, scale = 1.4) {
  const lib = await getPdfjs()
  const data = await file.arrayBuffer()
  const pdf = await lib.getDocument({ data }).promise
  const pages = []
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i)
    const viewport = page.getViewport({ scale })
    const canvas = document.createElement('canvas')
    canvas.width = viewport.width
    canvas.height = viewport.height
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    await page.render({ canvasContext: ctx, viewport }).promise
    pages.push(canvas.toDataURL('image/jpeg', 0.82))
  }
  return pages
}

// Render page 1 of a PDF blob to a PNG data URL — used to preview a processed
// PDF (e.g. after Edit PDF) in the result view.
export async function pdfFirstPagePreview(blob) {
  const lib = await getPdfjs()
  const data = await blob.arrayBuffer()
  const pdf = await lib.getDocument({ data }).promise
  const page = await pdf.getPage(1)
  const viewport = page.getViewport({ scale: 1.4 })
  const canvas = document.createElement('canvas')
  canvas.width = viewport.width
  canvas.height = viewport.height
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  await page.render({ canvasContext: ctx, viewport }).promise
  return canvas.toDataURL('image/png')
}

// ---- text-based conversions ----
export async function pdfToWord(file) {
  const lib = await getPdfjs()
  const data = await file.arrayBuffer()
  const pdf = await lib.getDocument({ data }).promise
  let text = ''
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i)
    const content = await page.getTextContent()
    text += content.items.map((it) => it.str).join(' ') + '\n\n'
  }
  // A .doc that Word opens: minimal HTML wrapper.
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word"><head><meta charset="utf-8"></head><body><pre style="font-family:Calibri,sans-serif;white-space:pre-wrap">${escapeHtml(
    text
  )}</pre></body></html>`
  return {
    blob: new Blob([html], { type: 'application/msword' }),
    name: `${baseName(file.name)}.doc`,
    mime: 'application/msword',
  }
}

export async function wordToPdf(file) {
  const name = (file.name || '').toLowerCase()
  let text = ''
  if (name.endsWith('.docx')) {
    // .docx is a zip extract the document text with mammoth.
    const mod = await import('mammoth/mammoth.browser')
    const mammoth = mod.default || mod
    const arrayBuffer = await file.arrayBuffer()
    const out = await mammoth.extractRawText({ arrayBuffer })
    text = out.value || ''
  } else if (name.endsWith('.doc')) {
    // Legacy binary .doc (OLE) recover readable text heuristically.
    text = extractLegacyDocText(await file.arrayBuffer())
  } else {
    text = await file.text()
  }
  text = text.replace(/<[^>]+>/g, '').trim()
  if (!text) throw new Error('Could not read any text from this document.')

  const doc = await PDFDocument.create()
  const font = await doc.embedFont(StandardFonts.Helvetica)
  const size = 11
  const margin = 50
  let page = doc.addPage()
  let { width, height } = page.getSize()
  let y = height - margin
  const maxWidth = width - margin * 2
  const lines = wrapText(sanitizeForPdf(text), font, size, maxWidth)
  for (const line of lines) {
    if (y < margin) {
      page = doc.addPage()
      y = page.getSize().height - margin
    }
    page.drawText(line, { x: margin, y, size, font, color: rgb(0.1, 0.1, 0.12) })
    y -= size * 1.5
  }
  const bytes = await doc.save()
  return {
    blob: new Blob([bytes], { type: 'application/pdf' }),
    name: `${baseName(file.name)}.pdf`,
    mime: 'application/pdf',
  }
}

function wrapText(text, font, size, maxWidth) {
  const out = []
  text.split(/\r?\n/).forEach((para) => {
    const words = para.split(/\s+/)
    let line = ''
    words.forEach((w) => {
      const test = line ? line + ' ' + w : w
      if (font.widthOfTextAtSize(test, size) > maxWidth && line) {
        out.push(line)
        line = w
      } else {
        line = test
      }
    })
    out.push(line)
  })
  return out
}

function escapeHtml(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

// Map smart punctuation to ASCII and drop anything the standard PDF font
// (WinAnsi) can't encode, so drawText never throws.
function sanitizeForPdf(s) {
  return s
    .replace(/[‘’‚‛]/g, "'")
    .replace(/[“”„]/g, '"')
    .replace(/[–—]/g, '-')
    .replace(/…/g, '...')
    .replace(/ /g, ' ')
    .replace(/\t/g, '    ')
    .replace(/[^\n\x20-\x7E\xA1-\xFF]/g, '')
}

// Recover readable text from a legacy binary .doc (OLE) file. Word stores the
// body as Windows-1252 or UTF-16LE; we pull out long runs of printable text
// and keep the ones that read like natural language, dropping binary noise.
function extractLegacyDocText(buf) {
  const bytes = new Uint8Array(buf)
  const runs = []
  let run = ''
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i]
    if (b === 0) continue // UTF-16LE padding don't break the run
    if (b === 10 || b === 13 || (b >= 32 && b <= 126) || (b >= 0xa0 && b <= 0xff)) {
      run += String.fromCharCode(b)
    } else {
      if (run.trim().length >= 4) runs.push(run.trim())
      run = ''
    }
  }
  if (run.trim().length >= 4) runs.push(run.trim())

  // Keep runs that are mostly letters/spaces (real sentences), not table data.
  const good = runs.filter((r) => {
    const letters = (r.match(/[A-Za-z]/g) || []).length
    return letters >= 4 && letters / r.length > 0.4
  })
  const text = (good.length ? good : runs)
    .join('\n')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
  if (!text) throw new Error('Could not read text from this .doc file. Please save it as .docx or .txt.')
  return text
}

// Bundle multiple blobs into a stored (uncompressed) zip no dependency.
export async function blobsToZip(blobs, baseNm) {
  const files = await Promise.all(
    blobs.map(async (b, i) => ({
      name: `${baseNm}-page${i + 1}.jpg`,
      data: new Uint8Array(await b.arrayBuffer()),
    }))
  )
  return buildZip(files)
}

function crc32(buf) {
  let c
  const table = crc32.table || (crc32.table = makeCrcTable())
  let crc = 0xffffffff
  for (let i = 0; i < buf.length; i++) {
    c = (crc ^ buf[i]) & 0xff
    crc = (crc >>> 8) ^ table[c]
  }
  return (crc ^ 0xffffffff) >>> 0
}
function makeCrcTable() {
  const t = []
  for (let n = 0; n < 256; n++) {
    let c = n
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    t[n] = c >>> 0
  }
  return t
}

function buildZip(files) {
  const enc = new TextEncoder()
  const chunks = []
  const central = []
  let offset = 0
  files.forEach((f) => {
    const nameBytes = enc.encode(f.name)
    const crc = crc32(f.data)
    const size = f.data.length
    const local = new Uint8Array(30 + nameBytes.length)
    const dv = new DataView(local.buffer)
    dv.setUint32(0, 0x04034b50, true)
    dv.setUint16(4, 20, true)
    dv.setUint16(6, 0, true)
    dv.setUint16(8, 0, true)
    dv.setUint16(10, 0, true)
    dv.setUint16(12, 0, true)
    dv.setUint32(14, crc, true)
    dv.setUint32(18, size, true)
    dv.setUint32(22, size, true)
    dv.setUint16(26, nameBytes.length, true)
    dv.setUint16(28, 0, true)
    local.set(nameBytes, 30)
    chunks.push(local, f.data)

    const cen = new Uint8Array(46 + nameBytes.length)
    const cdv = new DataView(cen.buffer)
    cdv.setUint32(0, 0x02014b50, true)
    cdv.setUint16(4, 20, true)
    cdv.setUint16(6, 20, true)
    cdv.setUint32(16, crc, true)
    cdv.setUint32(20, size, true)
    cdv.setUint32(24, size, true)
    cdv.setUint16(28, nameBytes.length, true)
    cdv.setUint32(42, offset, true)
    cen.set(nameBytes, 46)
    central.push(cen)
    offset += local.length + f.data.length
  })
  const centralSize = central.reduce((s, c) => s + c.length, 0)
  const end = new Uint8Array(22)
  const edv = new DataView(end.buffer)
  edv.setUint32(0, 0x06054b50, true)
  edv.setUint16(8, files.length, true)
  edv.setUint16(10, files.length, true)
  edv.setUint32(12, centralSize, true)
  edv.setUint32(16, offset, true)
  return new Blob([...chunks, ...central, end], { type: 'application/zip' })
}
